import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "src/components/Axios";
import { findAdminCategorySeed, CATEGORY_OFFERS_SLUG } from "src/config/adminCategorySeeds";

/**
 * Локальні пресети лише для ендпоінта `categories`.
 * Тримаємо в цьому ж файлі (а не глобально), щоб не було плутанини
 * з іншими запитами (products, orders, etc.), де параметри інші.
 */

const CATEGORY_API_PARAMS_PRESETS = Object.freeze({
   DEFAULT: Object.freeze({
      "g[0]": "cat:read",
      "g[1]": "cat:admin",
      "g[2]": "cat:categories",
   }),

   // приклад на майбутнє (якщо треба буде урізаний запит)
   PUBLIC: Object.freeze({
      "g[0]": "cat:read",
   }),

   // приклад: якщо потрібно явно "адмінський" набір
   ADMIN: Object.freeze({
      "g[0]": "cat:read",
      "g[1]": "cat:admin",
      "g[2]": "cat:categories",
   }),
});

/**
 * useCategoryData(categoryInput, options)
 *
 * ------------------------------------------------------------
 * Призначення:
 * - зробити запит "categories" по slug
 * - знайти categoryId
 * - для "пропозиції" додатково сформувати arrOfIgnoredCategoriesIds:
 *   [parentId, ...taxonomyChildrenIds]
 *
 * Вхід:
 * - categoryInput: slug або name (наприклад "propoziciyi" або "пропозиції")
 *
 * options:
 * - presetKey: ключ пресету з CATEGORY_API_PARAMS_PRESETS (DEFAULT | PUBLIC | ADMIN)
 * - paramsOverride: { ... } перезапис/додавання параметрів (наприклад g[3], page, тощо)
 *
 * Повертає завжди:
 * {
 *   categoryId: number|null,
 *   arrOfIgnoredCategoriesIds: number[],
 *   isLoadingGetCategoryData: boolean
 * }
 *
 * Правила:
 * - Якщо slug === CATEGORY_OFFERS_SLUG ("Пропозиції") => заповнюємо arrOfIgnoredCategoriesIds.
 * - Інакше arrOfIgnoredCategoriesIds = [] (для дочірніх категорій теж).
 */
export default function useCategoryData(categoryInput, options = {}) {
   const urlApi = "categories";
   const { presetKey = "DEFAULT", paramsOverride = {} } = options;

   // 1) Знайти seed тщ slug/name (як довідник)
   const resolvedSeed = useMemo(
      () => findAdminCategorySeed(categoryInput),
      [categoryInput]
   );

   // 2) Визначити slug: або із seed, або те що передали
   const slug = useMemo(() => {
      const direct = String(categoryInput ?? "").trim();
      return (resolvedSeed?.slug || direct) || "";
   }, [resolvedSeed, categoryInput]);

   // 3) ApiParams: preset + override + slug
   const ApiParams = useMemo(() => {
      const preset = CATEGORY_API_PARAMS_PRESETS[presetKey] || CATEGORY_API_PARAMS_PRESETS.DEFAULT;

      return {
         ...preset,
         ...paramsOverride,
         slug,
      };
   }, [presetKey, paramsOverride, slug]);

   const [arrOfIgnoredCategoriesIds, setArrOfIgnoredCategoriesIds] = useState([]);
   const [categoryId, setCategoryId] = useState(null);
   const [isLoadingGetCategoryData, setIsLoadingGetCategoryData] = useState(false);

   useEffect(() => {
      if (!slug) {
         setCategoryId(null);
         setArrOfIgnoredCategoriesIds([]);
         return;
      };

      let isMounted = true;

      const getCategory = async () => {
         if (!isMounted) return;

         setIsLoadingGetCategoryData(true);
         try {
            const response = await AxiosInstance.get(urlApi, { params: ApiParams });
            const data = response?.data?.["hydra:member"][0];
            const id = data?.id ?? null;

            if (!isMounted) return;

            setCategoryId(id);

            if (slug === CATEGORY_OFFERS_SLUG) {
               const taxonomyIds = (data?.categories ?? [])
                  .filter((elem) => elem?.type === "taxonomy")
                  .map((e) => e?.id)
                  .filter(Boolean);

               const ignoredCategories = [id, ...taxonomyIds].filter(Boolean);
               setArrOfIgnoredCategoriesIds(ignoredCategories);
            } else {
               setArrOfIgnoredCategoriesIds([]);
            };
         } catch (error) {
            console.error("Помилка запиту за даними категоріїЖ ", error);
            if (isMounted) {
               setCategoryId(null);
               setArrOfIgnoredCategoriesIds([]);
            };
         } finally {
            if (isMounted) setIsLoadingGetCategoryData(false);
         };
      };

      getCategory();

      return () => {
         isMounted = false;
      };
      // }, [slug, ApiParams]);
   }, [slug]);

   return { categoryId, arrOfIgnoredCategoriesIds, isLoadingGetCategoryData };
};